import React, { useState } from "react";
import { T } from "../theme";
import Banner from "../components/Banner";
import NowBand from "./NowBand";
import Roadmap from "./Roadmap";
import Statement from "./Statement";
import Pill, { BILLED_TONE } from "./Pill";
import SessionBlock from "../finance/SessionBlock";
import RetainerCard from "../finance/RetainerCard";
import RateCardCard from "../finance/RateCardCard";
import { typeLabel } from "../finance/presets";
import { fmtMoney, taxSuffix } from "../finance/money";
import { currentPhase, nextUp, headlineNumber, shortDate, clientSteps } from "./now";
import { FROM_DEFAULT } from "../invoice/house";

// The read-only client page: one scroll, no tabs, nothing that writes.
// Spec: docs/superpowers/specs/2026-10-17-client-page-release-1-design.md
export default function ClientView({ fields, cfg, configProblems = [], steps: storedSteps, finance, upcoming = [], extras = [], blockSessions = [], blockHistory = [], currentMonth, today }) {
  const typeLabelText = typeLabel(cfg.engagementType || fields["Engagement Type"]) || undefined;
  // Thread-only steps never reach this page; Pre-engagement shows on a demo.
  const { steps, phases } = clientSteps(storedSteps, { demo: fields.Demo === true });
  const phase = currentPhase(steps, typeLabelText, phases);
  const next = nextUp({ steps, cfg, finance, blockSessions, today, phases });
  const number = headlineNumber(cfg, { blockSessions });
  const m = cfg.modules || {};
  const hasLedger = !!(m.recurringSessions || m.rateCard || m.drawdownPool);
  const hasAccount = Object.keys(m).length > 0;
  // The standing arrangement (retainer card, rate card) is reference, not
  // news: folded behind "What's included" so the months start straight
  // under the heading (Alex, 29 Oct 2026).
  const [showIncluded, setShowIncluded] = useState(false);
  const hasIncluded = !!(m.retainer || m.rateCard);
  const accountSub = m.sessionBlock ? (m.sessionBlock.label || "Coaching block") : m.retainer ? `Retainer ${fmtMoney(m.retainer.monthlyTotal || m.retainer.amount || 0)} a month${taxSuffix(cfg)}` : null;

  // Each section is a full-width band; the page background and a near-white
  // alternate so sections read apart without boxes (design review 2).
  const Band = ({ tone, children, style }) => (
    <section style={{ background: tone === "light" ? T.canvas : "transparent", ...(style || {}) }}>
      <div className="hub-main" style={{ maxWidth: 840, margin: "0 auto", padding: "48px 24px" }}>{children}</div>
    </section>
  );

  return (
    <div style={{ fontSize: 15 }}>
      {configProblems.length > 0 && (
        <div className="hub-main" style={{ maxWidth: 840, margin: "0 auto", padding: "16px 24px 0" }}><Banner tone="warn" message="Some figures below may be wrong while we fix a setup problem. Contact Thread if anything looks off." /></div>
      )}
      <Band>
        <NowBand phase={phase} next={next} number={number} />
      </Band>
      <Band tone="light"><Roadmap steps={steps} visiblePhases={phases} today={today} cfg={cfg} /></Band>
      {hasAccount && (
        <Band><section>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: T.root, margin: 0 }}>Your account</h2>
            <span style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
              {accountSub && <span style={{ fontSize: 13, color: T.fgMuted }}>{accountSub}</span>}
            </span>
          </div>
          {m.sessionBlock && <SessionBlock block={m.sessionBlock} sessions={blockSessions} history={blockHistory} readOnly />}
          {m.sessionBlock && extras.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: T.root, margin: "0 0 4px" }}>Additional work</h3>
              {extras.map(x => { const b = BILLED_TONE[x.status || "to-invoice"] || BILLED_TONE["to-invoice"]; return (
                <div key={x.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, padding: "8px 0", borderTop: `1px solid ${T.borderSubtle}` }}>
                  <span>{x.label}{x.date && <span style={{ fontSize: 13, color: T.fgMuted }}> · {shortDate(x.date)}</span>}</span>
                  <span style={{ display: "flex", gap: 8, alignItems: "baseline" }}><span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmtMoney(x.amount)}</span><Pill tone={b[0]}>{b[1]}</Pill></span>
                </div>
              ); })}
            </div>
          )}
          {hasIncluded && (
            // Collapsed: the top of the card peeks out under a fade so it is
            // clearly there; the toggle sits on the fold. Expanded: the whole
            // retainer card and the rate card.
            <div style={{ position: "relative", marginBottom: 20 }}>
              <div style={{ maxHeight: showIncluded ? "none" : 84, overflow: "hidden" }}>
                {m.retainer && <RetainerCard retainer={m.retainer} drawdownPool={m.drawdownPool} cfg={cfg} />}
                {m.rateCard && <RateCardCard rates={m.rateCard.rates} />}
              </div>
              {!showIncluded && <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 56, background: `linear-gradient(rgba(255,255,255,0), ${T.bgSurface})`, pointerEvents: "none" }} />}
              <div style={{ display: "flex", justifyContent: "center", marginTop: showIncluded ? 4 : -14, position: "relative" }}>
                <button onClick={() => setShowIncluded(v => !v)} aria-expanded={showIncluded} style={{ background: T.canvas, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusPill, padding: "5px 16px", cursor: "pointer", fontFamily: T.fontSans, fontSize: 13, fontWeight: 600, color: T.fgSecondary, boxShadow: T.shadowSm }}>{showIncluded ? "Hide what's included ▴" : "What's included ▾"}</button>
              </div>
            </div>
          )}
          {hasLedger && <Statement cfg={cfg} finance={finance} currentMonth={currentMonth} today={today} />}
          {m.drawdownPool && upcoming.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: T.root, margin: "0 0 2px" }}>Planned</h3>
              <p style={{ fontSize: 13, color: T.fgMuted, margin: "0 0 4px" }}>Costs we expect to come from your fund, not yet dated</p>
              {upcoming.map(u => <div key={u.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: `1px solid ${T.borderSubtle}` }}><span>{u.label}</span><span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmtMoney(u.amount)}</span></div>)}
            </div>
          )}
        </section></Band>
      )}
      <Band tone="light" style={{ borderTop: `1px solid ${T.borderSubtle}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap", fontSize: 12, color: T.fgMuted }}>
          <span>Questions? {FROM_DEFAULT.contact}</span>
          <span style={{ fontWeight: 700, color: T.root, fontSize: 13 }}>Thread</span>
        </div>
      </Band>
    </div>
  );
}
